export const translations = {
  en: {
    // Hero Section
    hero: {
      title: 'I translate complex questions into elegant solutions',
      subtitle: 'Design • AI • Code • Concept',
      steps: {
        question: 'Question',
        concept: 'Concept',
        design: 'Design',
        solution: 'Solution',
      },
      valueProps: {
        design: 'Design + AI + Code',
        designDesc: 'Seamless integration of disciplines',
        structure: 'Structure & Efficiency',
        structureDesc: 'Logical approach to complex problems',
        concept: 'Concept to Reality',
        conceptDesc: 'From idea to working solution',
      },
      cta: 'Explore my expertise',
    },
    // Apps
    apps: {
      expertise: 'Expertise',
      portfolio: 'Portfolio',
      process: 'Process',
      contact: 'Contact',
      settings: 'Settings',
      aiAssistant: 'AI Assistant',
    },
    // Expertise app
    expertise: {
      title: 'Expertise & Tools',
      description:
        'I work at the intersection of design, AI and user experience. My strength lies in understanding systems, but thinking like a designer.',
      translationTitle: 'My Translation Process',
      translationDescription:
        'I translate complex questions into effective solutions through a structured process that combines technology, design and strategy.',
      domainsTitle: 'Expertise Areas',
      domains: {
        'ux-design': {
          title: 'Design & UX',
          problemTitle: 'Focus',
          problem: 'Creating intuitive and emotional digital experiences',
          approachTitle: 'My Approach',
          approach:
            'UX concepting and flows, visual direction, storytelling through interface, emotion in digital interactions',
          skillsTitle: 'Skills',
        },
        'ai-integration': {
          title: 'AI & Creation',
          problemTitle: 'Focus',
          problem:
            'Combining AI with creative storytelling and visual identity',
          approachTitle: 'My Approach',
          approach:
            'Character building & narrative AI, AI video and animation, AI-driven app prototypes, integration of data, logic and visual context',
          skillsTitle: 'Skills',
        },
        development: {
          title: 'Technology & Development',
          problemTitle: 'Focus',
          problem: 'Understanding and implementing technical systems',
          approachTitle: 'My Approach',
          approach:
            'Understanding React components and structure, fine-tuning front-end behavior, API data interpretation, functional thinking in web apps',
          skillsTitle: 'Skills',
        },
        'systems-thinking': {
          title: 'Strategy & Concept',
          problemTitle: 'Focus',
          problem: 'From idea to functional prototype',
          approachTitle: 'My Approach',
          approach:
            'Process analysis and optimization, from idea to functional prototype, combining creativity with technical feasibility',
          skillsTitle: 'Skills',
        },
      },
      skills: {
        'ui-design': 'UI Design',
        'user-research': 'User Research',
        figma: 'Figma',
        accessibility: 'Accessibility',
        'ai-ml': 'AI/ML Integration',
        'data-viz': 'Data Visualization',
        python: 'Python',
        'prompt-engineering': 'Prompt Engineering',
        react: 'React',
        typescript: 'TypeScript',
        tailwind: 'Tailwind CSS',
        performance: 'Performance Optimization',
        'design-systems': 'Design Systems',
        'workflow-optimization': 'Workflow Optimization',
        'cross-disciplinary': 'Cross-disciplinary Collaboration',
        'systems-architecture': 'Systems Architecture',
      },
    },
    // Portfolio app
    portfolio: {
      title: 'Projects & Creations',
      subtitle:
        'A selection of projects where I combine AI, design and UX to bring ideas to life — from practical apps to emotionally charged digital worlds.',
      filterBy: 'Filter by:',
      featuredProjects: 'Featured Projects',
      allProjects: 'All Projects',
      viewCaseStudy: 'View Case Study',
      backToProjects: 'Back to Projects',
      projectOverview: 'Project Overview',
      myRole: 'My Role',
      challengesAndSolutions: 'Challenges & Solutions',
      visitProject: 'Visit Live Project',
      viewCode: 'View Code',
      projects: {
        bulkrr: {
          title: 'BULKRR',
          description:
            'An AI-driven grocery app that helps users shop smarter, based on price, inventory, offers and consumption.',
          longDescription:
            'An AI-driven grocery app that helps users shop smarter. The app analyzes prices, inventory, offers and consumption patterns to provide personalized recommendations.',
          role: 'Concept Development, User Flow, AI Logic, UI Direction',
          challenges:
            'Creating an intuitive interface that makes complex AI recommendations feel simple and trustworthy. We focused on transparency in AI decision-making and clear visual feedback.',
          tags: ['AI', 'UX', 'App Development', 'Data Integration'],
          metrics: {
            efficiency: 'Shopping efficiency',
            savings: 'Average savings',
            satisfaction: 'User satisfaction',
          },
        },
        olympia: {
          title: 'Olympia Camping Group App',
          description:
            'A user-friendly web app for guests and managers of campsites, with features for information, communication and practical services.',
          longDescription:
            'A comprehensive web application designed for camping guests and site managers. The app provides seamless communication, practical services, and information access while maintaining brand consistency.',
          role: 'UX Design, Structure, AI Prototype via Magic Patterns',
          challenges:
            'Balancing the needs of two different user groups (guests and managers) while maintaining a consistent and intuitive interface. We created separate but harmonious experiences for each user type.',
          tags: ['UX', 'Hospitality', 'Web App', 'Design Systems'],
          metrics: {
            usability: 'Ease of use',
            engagement: 'Guest engagement',
            efficiency: 'Manager efficiency',
          },
        },
        aiCharacters: {
          title: 'AI Characters (TikTok)',
          description:
            'A series of digital characters with unique personalities, emotions and storylines. Created with AI tools like Kling AI and Flux Pro Ultra.',
          longDescription:
            'A creative project exploring the boundaries of AI-generated characters and storytelling. Each character has a unique personality, emotional depth, and visual identity, brought to life through advanced AI animation tools.',
          role: 'Character Building, Animation Prompting, Visual Direction',
          challenges:
            'Maintaining emotional authenticity and character consistency across AI-generated animations. We developed a systematic approach to prompt engineering that preserves character essence while allowing creative variation.',
          tags: ['AI', 'Character Design', 'Animation', 'Storytelling'],
          metrics: {
            engagement: 'Viewer engagement',
            consistency: 'Character consistency',
            views: 'Total views',
          },
        },
        horecaOptimization: {
          title: 'Horeca Optimization Document',
          description:
            'A strategic document focused on revenue increase, innovation and customer satisfaction within the hospitality industry.',
          longDescription:
            'A comprehensive strategic analysis and optimization plan for hospitality businesses. The document provides actionable insights for improving operations, increasing revenue, and enhancing customer satisfaction.',
          role: 'Analysis, Structure, Advisory Formulation',
          challenges:
            'Translating complex business data into clear, actionable recommendations that work for diverse hospitality contexts. We created a modular framework that adapts to different business sizes and types.',
          tags: [
            'Strategy',
            'Business Analysis',
            'Hospitality',
            'Process Optimization',
          ],
          metrics: {
            revenue: 'Revenue increase potential',
            satisfaction: 'Customer satisfaction impact',
            implementation: 'Implementation success rate',
          },
        },
        rijsSlim: {
          title: 'RijsSlim Prototype',
          description:
            'A concept for an app that supports traffic safety and driving skills through smart feedback and AI.',
          longDescription:
            'An educational app concept designed to improve driving skills and traffic safety. The app uses AI to provide personalized feedback, track progress, and adapt to individual learning needs.',
          role: 'Concept, Design, Structure and End-user Experience',
          challenges:
            'Creating an educational experience that feels supportive rather than judgmental. We focused on positive reinforcement and clear progress visualization to keep users motivated.',
          tags: ['UX', 'Education', 'AI', 'Mobile App'],
          metrics: {
            improvement: 'Skill improvement',
            engagement: 'User engagement',
            safety: 'Safety awareness increase',
          },
        },
      },
    },
    // Process app
    process: {
      title: 'My Process',
      description:
        'I work iteratively, visually and with a focus on experience. Every project starts with a clear goal and ends with a concept that is technically sound and feels right.',
      activitiesTitle: 'Activities',
      benefitsTitle: 'Client Benefits',
      steps: {
        discover: {
          title: 'Insight & Context',
          description:
            'Understanding the user, the goal and the context. Every idea starts with observation and analysis.',
          activities: [
            'User observation and research',
            'Context analysis',
            'Goal definition',
            'Stakeholder alignment',
          ],
          benefits: [
            'Clear understanding of real needs',
            'Aligned expectations from the start',
            'Prevention of unnecessary complexity',
            'Strong foundation for design decisions',
          ],
        },
        define: {
          title: 'Concept & Direction',
          description:
            'Translating insights into a visual and functional concept. Storytelling and UX go hand in hand here.',
          activities: [
            'Conceptual exploration',
            'Visual direction setting',
            'Storytelling framework',
            'UX strategy development',
          ],
          benefits: [
            'Clear creative direction',
            'Aligned vision across stakeholders',
            'Innovative yet feasible concepts',
            'Strong narrative foundation',
          ],
        },
        design: {
          title: 'Prototype & Validation',
          description:
            'Building AI or UX prototypes using tools like Magic Patterns, with focus on flow and feel.',
          activities: [
            'Interactive prototyping',
            'Flow validation',
            'User testing',
            'Iterative refinement',
          ],
          benefits: [
            'Early validation of concepts',
            'Reduced development risks',
            'User-tested interactions',
            'Clear development roadmap',
          ],
        },
        develop: {
          title: 'Fine-tuning & Consistency',
          description:
            'Visual alignment, micro-interactions, and UX detailing for a seamless experience.',
          activities: [
            'Visual polish',
            'Micro-interaction design',
            'Consistency checking',
            'Accessibility refinement',
          ],
          benefits: [
            'Professional, polished result',
            'Consistent user experience',
            'Attention to detail',
            'Accessible for all users',
          ],
        },
        deliver: {
          title: 'AI Integration & Realization',
          description:
            'Applying AI to connect behavior, data and creativity. Always with a human core.',
          activities: [
            'AI implementation',
            'Data integration',
            'Behavioral optimization',
            'Human-centered refinement',
          ],
          benefits: [
            'Intelligent, adaptive solutions',
            'Data-driven insights',
            'Scalable technology',
            'Human-centered AI',
          ],
        },
      },
    },
    // Contact app
    contact: {
      title: 'Contact',
      getInTouch: 'Get in touch',
      description:
        "I'm always looking for interesting projects and collaborations. Contact me to discuss how I can help translate your complex challenges into elegant solutions.",
      sendMessage: 'Send a message',
      form: {
        name: 'Name',
        email: 'Email',
        subject: 'Subject',
        message: 'Message',
        submit: 'Send message',
      },
      success: {
        title: 'Message sent!',
        message:
          'Thank you for your message. I will get back to you as soon as possible.',
        newMessage: 'New message',
      },
    },
    // Translation demo
    translationDemo: {
      title: 'How I Translate',
      steps: {
        problem: {
          title: 'Client Need',
          description: 'We want an intuitive way to visualize data',
        },
        analysis: {
          title: 'Analysis',
          description:
            'Identification of the real need: accessibility of complex data for non-technical users',
        },
        approach: {
          title: 'Approach',
          description:
            'Combination of AI pattern recognition with interactive visualizations and context-sensitive filters',
        },
        solution: {
          title: 'Solution',
          description:
            'A dashboard that automatically structures and presents complex data based on user context',
        },
      },
    },
    // Common
    common: {
      back: 'Back',
      cancel: 'Cancel',
      apply: 'Apply',
      search: 'Search',
      all: 'All',
      previous: 'Previous',
      next: 'Next',
      close: 'Close',
    },
    // Desktop
    desktop: {
      customizeBackground: 'Customize Background',
    },
    // Background Customizer
    backgroundCustomizer: {
      title: 'Customize Background',
      backgrounds: {
        default: 'Purple Dream',
        ocean: 'Ocean Blue',
        sunset: 'Sunset Orange',
        forest: 'Forest Green',
        midnight: 'Midnight',
        aurora: 'Aurora',
      },
    },
    // Command Palette
    commandPalette: {
      placeholder: 'Type a command or search...',
      open: 'Open',
    },
    // Projects
    projects: {
      title: 'Projects',
      viewProject: 'View Project',
      code: 'Code',
    },
    // Skills
    skills: {
      title: 'T-Shaped Skills',
      broadKnowledge: 'Broad Knowledge',
      deepSpecialization: 'Deep Specialization',
    },
    // Timeline
    timeline: {
      title: 'Career Timeline',
    },
    // Notes
    notes: {
      title: 'Notes',
      newNote: 'New Note',
      searchPlaceholder: 'Search notes...',
    },
    // Settings
    settings: {
      title: 'Settings',
      theme: 'Theme',
      themes: {
        dark: 'Dark',
        light: 'Light',
        neon: 'Neon',
      },
      language: 'Language',
      languages: {
        english: 'English',
        dutch: 'Nederlands',
      },
      accessibility: 'Accessibility',
      reduceMotion: 'Reduce Motion',
      highContrast: 'High Contrast',
      background: 'Background',
    },
    // AI Assistant
    aiAssistant: {
      title: 'AI Assistant',
      subtitle: 'Always here to help',
      placeholder: 'Ask me anything...',
      greeting:
        'Hello! I am your AI assistant. How can I help you explore MVL Creative?',
      simulatedResponse:
        'This is a simulated response. In a production environment, this would connect to an AI API for intelligent responses.',
    },
  },
  nl: {
    // Hero Section
    hero: {
      title: 'Ik vertaal complexe vragen naar elegante oplossingen',
      subtitle: 'Design • AI • Code • Concept',
      steps: {
        question: 'Vraag',
        concept: 'Concept',
        design: 'Design',
        solution: 'Oplossing',
      },
      valueProps: {
        design: 'Design + AI + Code',
        designDesc: 'Naadloze integratie van disciplines',
        structure: 'Structuur & Efficiëntie',
        structureDesc: 'Logische aanpak voor complexe problemen',
        concept: 'Concept naar Realiteit',
        conceptDesc: 'Van idee naar werkende oplossing',
      },
      cta: 'Bekijk mijn expertise',
    },
    // Apps
    apps: {
      expertise: 'Expertise',
      portfolio: 'Portfolio',
      process: 'Werkwijze',
      contact: 'Contact',
      settings: 'Instellingen',
      aiAssistant: 'AI Assistent',
    },
    // Expertise app
    expertise: {
      title: 'Expertise & Tools',
      description:
        'Ik werk op het snijvlak van design, AI en gebruikerservaring. Mijn kracht ligt in het begrijpen van systemen, maar denken als een ontwerper.',
      translationTitle: 'Mijn Vertaalproces',
      translationDescription:
        'Ik vertaal complexe vragen naar effectieve oplossingen door een gestructureerd proces dat technologie, design en strategie combineert.',
      domainsTitle: 'Expertise Gebieden',
      domains: {
        'ux-design': {
          title: 'Design & UX',
          problemTitle: 'Focus',
          problem: 'Intuïtieve en emotionele digitale ervaringen creëren',
          approachTitle: 'Mijn Aanpak',
          approach:
            'UX-concepting en flows, visual direction, storytelling door interface, emotie in digitale interacties',
          skillsTitle: 'Vaardigheden',
        },
        'ai-integration': {
          title: 'AI & Creatie',
          problemTitle: 'Focus',
          problem:
            'AI combineren met creatieve storytelling en visuele identiteit',
          approachTitle: 'Mijn Aanpak',
          approach:
            'Character building & narratieve AI, AI-video en animatie, AI-driven app prototypes, integratie van data, logica en visuele context',
          skillsTitle: 'Vaardigheden',
        },
        development: {
          title: 'Technologie & Development',
          problemTitle: 'Focus',
          problem: 'Technische systemen begrijpen en implementeren',
          approachTitle: 'Mijn Aanpak',
          approach:
            'Begrip van React-componenten en structuur, fine-tuning van front-end gedrag, API-data interpretatie, functioneel denken in webapps',
          skillsTitle: 'Vaardigheden',
        },
        'systems-thinking': {
          title: 'Strategie & Concept',
          problemTitle: 'Focus',
          problem: 'Van idee tot functioneel prototype',
          approachTitle: 'Mijn Aanpak',
          approach:
            'Procesanalyse en -optimalisatie, van idee tot functioneel prototype, combineren van creativiteit en technische haalbaarheid',
          skillsTitle: 'Vaardigheden',
        },
      },
      skills: {
        'ui-design': 'UI Design',
        'user-research': 'User Research',
        figma: 'Figma',
        accessibility: 'Toegankelijkheid',
        'ai-ml': 'AI/ML Integratie',
        'data-viz': 'Data Visualisatie',
        python: 'Python',
        'prompt-engineering': 'Prompt Engineering',
        react: 'React',
        typescript: 'TypeScript',
        tailwind: 'Tailwind CSS',
        performance: 'Performance Optimalisatie',
        'design-systems': 'Design Systems',
        'workflow-optimization': 'Workflow Optimalisatie',
        'cross-disciplinary': 'Cross-disciplinaire Samenwerking',
        'systems-architecture': 'Systeem Architectuur',
      },
    },
    // Portfolio app
    portfolio: {
      title: 'Projecten & Creaties',
      subtitle:
        'Een selectie van projecten waarin ik AI, design en UX combineer om ideeën tot leven te brengen — van praktische apps tot emotioneel geladen digitale werelden.',
      filterBy: 'Filter op:',
      featuredProjects: 'Uitgelichte Projecten',
      allProjects: 'Alle Projecten',
      viewCaseStudy: 'Bekijk Case Study',
      backToProjects: 'Terug naar Projecten',
      projectOverview: 'Project Overzicht',
      myRole: 'Mijn Rol',
      challengesAndSolutions: 'Uitdagingen & Oplossingen',
      visitProject: 'Bekijk Live Project',
      viewCode: 'Bekijk Code',
      projects: {
        bulkrr: {
          title: 'BULKRR',
          description:
            'Een AI-gedreven boodschappenapp die gebruikers helpt slimmer in te kopen, op basis van prijs, voorraad, aanbiedingen en verbruik.',
          longDescription:
            'Een AI-gedreven boodschappenapp die gebruikers helpt slimmer in te kopen. De app analyseert prijzen, voorraad, aanbiedingen en verbruikspatronen om gepersonaliseerde aanbevelingen te doen.',
          role: 'Conceptontwikkeling, User Flow, AI-logica, UI-richting',
          challenges:
            'Het creëren van een intuïtieve interface die complexe AI-aanbevelingen eenvoudig en betrouwbaar maakt. We focusten op transparantie in AI-besluitvorming en duidelijke visuele feedback.',
          tags: ['AI', 'UX', 'App-ontwikkeling', 'Data-integratie'],
          metrics: {
            efficiency: 'Winkel efficiëntie',
            savings: 'Gemiddelde besparing',
            satisfaction: 'Gebruikerstevredenheid',
          },
        },
        olympia: {
          title: 'Olympia Camping Group App',
          description:
            'Een gebruiksvriendelijke webapp voor gasten en beheerders van campings, met functies voor informatie, communicatie en praktische services.',
          longDescription:
            'Een uitgebreide webapplicatie ontworpen voor campinggasten en terreinbeheerders. De app biedt naadloze communicatie, praktische services en informatietoegang met behoud van merkconsistentie.',
          role: 'UX-design, Structuur, AI-prototype via Magic Patterns',
          challenges:
            'Het balanceren van de behoeften van twee verschillende gebruikersgroepen (gasten en beheerders) met behoud van een consistente en intuïtieve interface. We creëerden aparte maar harmonieuze ervaringen voor elk gebruikerstype.',
          tags: ['UX', 'Hospitality', 'Web App', 'Design Systems'],
          metrics: {
            usability: 'Gebruiksgemak',
            engagement: 'Gast betrokkenheid',
            efficiency: 'Beheerder efficiëntie',
          },
        },
        aiCharacters: {
          title: 'AI Characters (TikTok)',
          description:
            'Een reeks digitale karakters met unieke persoonlijkheden, emoties en verhaallijnen. Gecreëerd met AI-tools zoals Kling AI en Flux Pro Ultra.',
          longDescription:
            'Een creatief project dat de grenzen van AI-gegenereerde karakters en storytelling verkent. Elk karakter heeft een unieke persoonlijkheid, emotionele diepte en visuele identiteit, tot leven gebracht door geavanceerde AI-animatietools.',
          role: 'Character Building, Animatieprompting, Visuele Regie',
          challenges:
            'Het behouden van emotionele authenticiteit en karakterconsistentie in AI-gegenereerde animaties. We ontwikkelden een systematische aanpak voor prompt engineering die de essentie van karakters behoudt terwijl creatieve variatie mogelijk blijft.',
          tags: ['AI', 'Character Design', 'Animatie', 'Storytelling'],
          metrics: {
            engagement: 'Kijkersbetrokkenheid',
            consistency: 'Karakterconsistentie',
            views: 'Totaal aantal views',
          },
        },
        horecaOptimization: {
          title: 'Horeca Optimalisatiedocument',
          description:
            'Een strategisch document gericht op omzetverhoging, innovatie en klanttevredenheid binnen de horeca.',
          longDescription:
            'Een uitgebreide strategische analyse en optimalisatieplan voor horecabedrijven. Het document biedt bruikbare inzichten voor het verbeteren van operaties, het verhogen van omzet en het verbeteren van klanttevredenheid.',
          role: 'Analyse, Structuur, Adviesformulering',
          challenges:
            'Het vertalen van complexe bedrijfsdata naar duidelijke, uitvoerbare aanbevelingen die werken voor diverse horecacontexten. We creëerden een modulair raamwerk dat zich aanpast aan verschillende bedrijfsgroottes en -typen.',
          tags: [
            'Strategie',
            'Bedrijfsanalyse',
            'Horeca',
            'Procesoptimalisatie',
          ],
          metrics: {
            revenue: 'Omzetstijging potentieel',
            satisfaction: 'Klanttevredenheid impact',
            implementation: 'Implementatie succespercentage',
          },
        },
        rijsSlim: {
          title: 'RijsSlim Prototype',
          description:
            'Een concept voor een app die verkeersveiligheid en rijvaardigheid ondersteunt via slimme feedback en AI.',
          longDescription:
            'Een educatief app-concept ontworpen om rijvaardigheden en verkeersveiligheid te verbeteren. De app gebruikt AI om gepersonaliseerde feedback te geven, voortgang bij te houden en zich aan te passen aan individuele leerbehoeften.',
          role: 'Concept, Design, Structuur en Eindgebruikerservaring',
          challenges:
            'Het creëren van een educatieve ervaring die ondersteunend aanvoelt in plaats van oordelend. We focusten op positieve bekrachtiging en duidelijke voortgangsvisualisatie om gebruikers gemotiveerd te houden.',
          tags: ['UX', 'Educatie', 'AI', 'Mobiele App'],
          metrics: {
            improvement: 'Vaardigheidsverbetering',
            engagement: 'Gebruikersbetrokkenheid',
            safety: 'Veiligheidsbewustzijn toename',
          },
        },
      },
    },
    // Process app
    process: {
      title: 'Mijn Werkwijze',
      description:
        'Ik werk iteratief, visueel en met een focus op beleving. Elk project begint met een duidelijk doel en eindigt met een concept dat technisch klopt én goed voelt.',
      activitiesTitle: 'Activiteiten',
      benefitsTitle: 'Voordelen voor de klant',
      steps: {
        discover: {
          title: 'Inzicht & Context',
          description:
            'Begrijpen van de gebruiker, het doel en de context. Elk idee start met observatie en analyse.',
          activities: [
            'Gebruikersobservatie en onderzoek',
            'Contextanalyse',
            'Doeldefinitie',
            'Stakeholder afstemming',
          ],
          benefits: [
            'Helder begrip van echte behoeften',
            'Afgestemde verwachtingen vanaf het begin',
            'Voorkomen van onnodige complexiteit',
            'Sterke basis voor ontwerpbeslissingen',
          ],
        },
        define: {
          title: 'Concept & Richting',
          description:
            'Vertalen van inzichten naar een visueel en functioneel concept. Storytelling en UX gaan hier hand in hand.',
          activities: [
            'Conceptuele verkenning',
            'Visuele richting bepalen',
            'Storytelling raamwerk',
            'UX-strategie ontwikkeling',
          ],
          benefits: [
            'Duidelijke creatieve richting',
            'Afgestemde visie tussen stakeholders',
            'Innovatieve maar haalbare concepten',
            'Sterke narratieve basis',
          ],
        },
        design: {
          title: 'Prototype & Validatie',
          description:
            'Bouwen van AI- of UX-prototypes via tools zoals Magic Patterns, met focus op flow en gevoel.',
          activities: [
            'Interactief prototyping',
            'Flow validatie',
            'Gebruikerstesten',
            'Iteratieve verfijning',
          ],
          benefits: [
            'Vroege validatie van concepten',
            "Verminderde ontwikkelrisico's",
            'Gebruikersgeteste interacties',
            'Duidelijk ontwikkelplan',
          ],
        },
        develop: {
          title: 'Fine-tuning & Consistentie',
          description:
            'Visuele afstemming, micro-interacties, en UX-detailing voor een naadloze ervaring.',
          activities: [
            'Visuele polish',
            'Micro-interactie design',
            'Consistentiecontrole',
            'Toegankelijkheid verfijning',
          ],
          benefits: [
            'Professioneel, gepolijst resultaat',
            'Consistente gebruikerservaring',
            'Aandacht voor detail',
            'Toegankelijk voor alle gebruikers',
          ],
        },
        deliver: {
          title: 'AI-integratie & Realisatie',
          description:
            'Toepassen van AI om gedrag, data en creativiteit te verbinden. Altijd met een menselijke kern.',
          activities: [
            'AI-implementatie',
            'Data-integratie',
            'Gedragsoptimalisatie',
            'Mensgerichte verfijning',
          ],
          benefits: [
            'Intelligente, adaptieve oplossingen',
            'Data-gedreven inzichten',
            'Schaalbare technologie',
            'Mensgerichte AI',
          ],
        },
      },
    },
    // Contact app
    contact: {
      title: 'Contact',
      getInTouch: 'Neem contact op',
      description:
        'Ik ben altijd op zoek naar interessante projecten en samenwerkingen. Neem contact met me op om te bespreken hoe ik kan helpen met het vertalen van jouw complexe uitdagingen naar elegante oplossingen.',
      sendMessage: 'Stuur een bericht',
      form: {
        name: 'Naam',
        email: 'Email',
        subject: 'Onderwerp',
        message: 'Bericht',
        submit: 'Verstuur bericht',
      },
      success: {
        title: 'Bericht verstuurd!',
        message:
          'Bedankt voor je bericht. Ik neem zo snel mogelijk contact met je op.',
        newMessage: 'Nieuw bericht',
      },
    },
    // Translation demo
    translationDemo: {
      title: 'Hoe ik vertaal',
      steps: {
        problem: {
          title: 'Klantbehoefte',
          description:
            'We willen een intuïtieve manier om data te visualiseren',
        },
        analysis: {
          title: 'Analyse',
          description:
            'Identificatie van de werkelijke behoefte: toegankelijkheid van complexe data voor niet-technische gebruikers',
        },
        approach: {
          title: 'Aanpak',
          description:
            'Combinatie van AI-patroonherkenning met interactieve visualisaties en contextgevoelige filters',
        },
        solution: {
          title: 'Oplossing',
          description:
            'Een dashboard dat complexe data automatisch structureert en presenteert op basis van gebruikerscontext',
        },
      },
    },
    // Common
    common: {
      back: 'Terug',
      cancel: 'Annuleren',
      apply: 'Toepassen',
      search: 'Zoeken',
      all: 'Alle',
      previous: 'Vorige',
      next: 'Volgende',
      close: 'Sluiten',
    },
    // Desktop
    desktop: {
      customizeBackground: 'Achtergrond Aanpassen',
    },
    // Background Customizer
    backgroundCustomizer: {
      title: 'Achtergrond Aanpassen',
      backgrounds: {
        default: 'Paarse Droom',
        ocean: 'Oceaan Blauw',
        sunset: 'Zonsondergang Oranje',
        forest: 'Bos Groen',
        midnight: 'Middernacht',
        aurora: 'Aurora',
      },
    },
    // Command Palette
    commandPalette: {
      placeholder: 'Typ een commando of zoek...',
      open: 'Open',
    },
    // Projects
    projects: {
      title: 'Projecten',
      viewProject: 'Bekijk Project',
      code: 'Code',
    },
    // Skills
    skills: {
      title: 'T-Vormige Vaardigheden',
      broadKnowledge: 'Brede Kennis',
      deepSpecialization: 'Diepe Specialisatie',
    },
    // Timeline
    timeline: {
      title: 'Carrière Tijdlijn',
    },
    // Notes
    notes: {
      title: 'Notities',
      newNote: 'Nieuwe Notitie',
      searchPlaceholder: 'Zoek notities...',
    },
    // Settings
    settings: {
      title: 'Instellingen',
      theme: 'Thema',
      themes: {
        dark: 'Donker',
        light: 'Licht',
        neon: 'Neon',
      },
      language: 'Taal',
      languages: {
        english: 'English',
        dutch: 'Nederlands',
      },
      accessibility: 'Toegankelijkheid',
      reduceMotion: 'Beweging Verminderen',
      highContrast: 'Hoog Contrast',
      background: 'Achtergrond',
    },
    // AI Assistant
    aiAssistant: {
      title: 'AI Assistent',
      subtitle: 'Altijd hier om te helpen',
      placeholder: 'Vraag me wat dan ook...',
      greeting:
        'Hallo! Ik ben je AI-assistent. Hoe kan ik je helpen MVL Creative te verkennen?',
      simulatedResponse:
        'Dit is een gesimuleerd antwoord. In een productieomgeving zou dit verbinding maken met een AI API voor intelligente antwoorden.',
    },
  },
}
export type Language = 'en' | 'nl'
export type TranslationKey = typeof translations.en
